import { useQuery } from '@apollo/client';

import GetCourseForCertificateQuery from 'bundles/accomplishments/components/course-certificates/queries/GetCourseForCertificateQuery.graphql';
import type {
  GetCourseForCertificateQuery as GetCourseForCertificateQueryType,
  GetCourseForCertificateQueryVariables,
} from 'bundles/accomplishments/components/course-certificates/queries/__generated__/GetCourseForCertificateQuery';

export type CertificateCourseData = {
  id: string;
  slug: string;
  name: string;
  description: string;
  workload: string;
  partnerLogo: string;
  partnerNames: string[];
};

type UseCourseForCertificateResult = {
  courseData: CertificateCourseData | undefined;
  loading: boolean;
  error: Error | undefined;
};

/**
 * Fetches course data needed for certificate display
 * @param courseId Course ID
 * @returns Course data including description, workload, and partner information
 */
const useCourseForCertificate = (courseId: string): UseCourseForCertificateResult => {
  const { data, loading, error } = useQuery<GetCourseForCertificateQueryType, GetCourseForCertificateQueryVariables>(
    GetCourseForCertificateQuery,
    {
      variables: {
        courseId,
      },
      context: { clientName: 'gatewayGql' },
      skip: !courseId,
    }
  );

  const courseResponse = data?.Course?.queryById;

  return {
    courseData: courseResponse
      ? {
          id: courseResponse.id,
          slug: courseResponse.slug,
          name: courseResponse.name,
          description: courseResponse.textDescription || '',
          workload: courseResponse.estimatedWorkload || '',
          partnerLogo: courseResponse.partners?.[0]?.squareLogo || '',
          partnerNames: courseResponse.partners?.map((partner) => partner.name) || [],
        }
      : undefined,
    loading,
    error,
  };
};

export default useCourseForCertificate;
